import { Injectable } from '@angular/core';
import { AbstractService } from './abstract-service';
import { TimerTools } from '../tools/timer-tools';

export interface Notification {
  id: string;
  type: NotificationType;
  text: string;
  textParams: Record<string, any>;
  count: number;
  createdAt: Date;
  closedAt?: Date;
  action?: NotificationAction;
  closeable: boolean;
}

export interface NotificationOptions {
  id: string;
  action: NotificationAction;
  textParams: Record<string, any>;
  closeable: boolean;
}

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface NotificationAction {
  text: string;
  action: () => void;
}

export interface NotificationState {
  notifications: Notification[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService extends AbstractService<NotificationState> {
  private static readonly timeToLiveSeconds = 8;
  private static readonly closeTimeSeconds = 1;

  private counter = 1;

  constructor() {
    super({
      notifications: [],
    });

    // Periodically recalculate notifications
    TimerTools.interval$(1000).subscribe(() => {
      this.update(NotificationService.recalculate);
    });
  }

  public show(type: NotificationType, text: string, options: Partial<NotificationOptions> = {}): void {
    this.update(state => {
      // Update existing notification if found
      const existingNotification = state.notifications.find(
        notification => notification.type === type && notification.text === text,
      );
      if (existingNotification && !existingNotification.closedAt) {
        return {
          notifications: state.notifications.map(notification => {
            if (existingNotification.id === notification.id) {
              return {
                ...notification,
                createdAt: new Date(),
                count: notification.count + 1,
              };
            }
            return notification;
          }),
        };
      }

      // Add new notification
      const notification = this.createNotification(type, text, options);
      return {
        notifications: [...state.notifications, notification],
      };
    }, NotificationService.recalculate);
  }

  public close(id: string): void {
    this.update(
      state => ({
        notifications: state.notifications.map(notification => {
          if (notification.closeable && notification.id === id && !notification.closedAt) {
            return {
              ...notification,
              closedAt: new Date(),
            };
          }
          return notification;
        }),
      }),
      NotificationService.recalculate,
    );
  }

  public doAction(id: string): void {
    const notification = this.state.notifications.find(notification => notification.id === id);
    if (!notification || !notification.action || !!notification.closedAt) {
      return;
    }
    notification.action.action();
  }

  private createNotification(
    type: NotificationType,
    text: string,
    options: Partial<NotificationOptions>,
  ): Notification {
    return {
      id: options?.id || 'notification' + this.counter++,
      type: type,
      text: text,
      textParams: options?.textParams || {},
      count: 1,
      createdAt: new Date(),
      closedAt: undefined,
      action: options?.action,
      closeable: options?.closeable ?? true,
    };
  }

  private static recalculate(state: NotificationState): NotificationState {
    const nowSeconds = new Date().getTime() / 1000;
    const aliveNotifications = state.notifications
      .map(notification => {
        // Closing expired notifications
        const createdSeconds = notification.createdAt.getTime() / 1000;
        const diffSeconds = nowSeconds - createdSeconds;
        const expired = diffSeconds > NotificationService.timeToLiveSeconds;
        if (!notification.closedAt && expired) {
          return {
            ...notification,
            closedAt: new Date(),
          };
        }
        return notification;
      })
      .filter(notification => {
        // Cleaning up closed notifications
        if (!notification.closedAt) {
          return true;
        }
        const createdSeconds = notification.closedAt.getTime() / 1000;
        const diffSeconds = nowSeconds - createdSeconds;
        return diffSeconds < NotificationService.closeTimeSeconds;
      });
    return {
      ...state,
      notifications: aliveNotifications,
    };
  }
}
